import React, { useEffect, useRef, useState } from "react";
import Styles from "./style.module.scss";
import { Pagination, Navigation, Autoplay } from "swiper";
import type { Swiper as SwiperInstance } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/scss";
import "swiper/scss/navigation";
import "swiper/scss/pagination";
import { arrSlides } from "../../utils/config";
import { IArrSlides } from "../../types";
import classNames from "classnames/bind";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { useWindowResize } from "../../hooks/useWindowResize";

type ImagePriorityProps = {
  fetchpriority?: "high" | "low" | "auto";
};

const slideAlts = [
  "Sleeping newborn baby girl wearing a soft pink tulle bow",
  "Sleeping newborn in a carefully styled studio portrait",
  "Newborn babies posed together in a creative studio portrait",
  "Newborn baby resting during a professional portrait session",
  "Wrapped newborn baby in a softly styled studio setting",
];

const eyebrowText = "Alena Lobacheva Photography";

export const SliderComponent: React.FC = () => {
  const { width } = useWindowResize();
  const prefersReducedMotion = useReducedMotion();
  const isPrerendering = Boolean(window.__PRERENDERING__);
  const hasPrerenderedHtml = document.documentElement.dataset.prerendered === "true";
  const [autoplayEnabled, setAutoplayEnabled] = useState(false);
  const swiperRef = useRef<SwiperInstance | null>(null);

  useEffect(() => {
    if (isPrerendering || autoplayEnabled) return;

    const enableAutoplay = () => setAutoplayEnabled(true);
    const interactionEvents: Array<keyof WindowEventMap> = ["pointerdown", "touchstart", "wheel", "keydown"];

    interactionEvents.forEach((eventName) =>
      window.addEventListener(eventName, enableAutoplay, { passive: true, once: true })
    );

    return () => interactionEvents.forEach((eventName) => window.removeEventListener(eventName, enableAutoplay));
  }, [autoplayEnabled, isPrerendering]);

  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper?.autoplay) return;

    if (!autoplayEnabled) {
      swiper.autoplay.stop();
      return;
    }

    const firstTransitionTimer = window.setTimeout(() => {
      swiper.slideNext();
      swiper.autoplay.start();
    }, 1500);

    return () => window.clearTimeout(firstTransitionTimer);
  }, [autoplayEnabled]);

  const handleChangeSlide = arrSlides.map((item: IArrSlides): string => {
    if (width <= 768 && item.mobile) {
      return item.mobile;
    } else {
      return item.desktop;
    }
  });

  const cx = classNames.bind(Styles);
  const stylePrevBtn = cx("swiperBtn", "swiperBtn_prev");
  const styleNextBtn = cx("swiperBtn", "swiperBtn_next");

  const welcomeContent = (
    <div className={Styles.slideShow__welcomeContainer}>
      <div className={Styles.slideShow__aboutMeContainer}>
        <motion.h1
          initial={isPrerendering || prefersReducedMotion ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: isPrerendering || prefersReducedMotion ? 0 : 0.2,
            duration: isPrerendering || prefersReducedMotion ? 0 : 0.75,
            ease: "easeOut",
          }}
          className={Styles.slideShow__welcomeTitle}
        >
          In-Home Newborn <br />
          Photographer
          <span> in Charlotte, NC</span>
        </motion.h1>
        <motion.p
          className={Styles.slideShow__eyebrow}
          initial={isPrerendering || prefersReducedMotion ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: isPrerendering || prefersReducedMotion ? 0 : 0.65,
            duration: isPrerendering || prefersReducedMotion ? 0 : 0.75,
            ease: "easeOut",
          }}
        >
          {eyebrowText}
        </motion.p>
        <motion.p
          initial={isPrerendering || prefersReducedMotion ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: isPrerendering || prefersReducedMotion ? 0 : 1.1,
            duration: isPrerendering || prefersReducedMotion ? 0 : 0.75,
            ease: "easeOut",
          }}
          className={Styles.slideShow__welcomeDescription}
        >
          Calm, baby-led newborn photography in the comfort of your home. I bring professional lighting, wraps and
          simple styling to create gently posed portraits and natural family moments.
        </motion.p>
        <motion.div
          className={Styles.slideShow__welcomeActions}
          initial={isPrerendering || prefersReducedMotion ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: isPrerendering || prefersReducedMotion ? 0 : 1.55,
            duration: isPrerendering || prefersReducedMotion ? 0 : 0.75,
            ease: "easeOut",
          }}
        >
          <Link className={Styles.slideShow__welcomeLink} to="/prices/newborn/">
            Explore newborn sessions
          </Link>
        </motion.div>
      </div>
      <div className={Styles.slideShow__linkAboutNewbornContainer} />
    </div>
  );

  if (isPrerendering) {
    return (
      <div className={Styles.slideShow} data-static-prerender-hero="true">
        {welcomeContent}
        <picture>
          <source media="(max-width: 768px)" srcSet={arrSlides[0].mobile} />
          <img
            className={Styles.slideShow__image}
            src={arrSlides[0].desktop}
            alt="Sleeping newborn baby girl wearing a soft pink tulle bow"
            width="1440"
            height="640"
            loading="eager"
            {...({ fetchpriority: "high" } as ImagePriorityProps)}
          />
        </picture>
      </div>
    );
  }

  return (
    <div className={Styles.slideShow}>
      {welcomeContent}
      <Swiper
        slidesPerView={1}
        spaceBetween={0}
        loop={true}
        speed={1000}
        autoplay={{
          delay: 3000,
          disableOnInteraction: false,
        }}
        pagination={{
          clickable: true,
        }}
        navigation={{
          prevEl: `.${Styles.swiperBtn_prev}`,
          nextEl: `.${Styles.swiperBtn_next}`,
        }}
        modules={[Autoplay, Pagination, Navigation]}
        className={Styles.mySwiper}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          if (!autoplayEnabled) swiper.autoplay.stop();
        }}
      >
        {handleChangeSlide.map((slide: string, index: number) => {
          return (
            <SwiperSlide key={index}>
              <motion.img
                initial={hasPrerenderedHtml ? false : { opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className={Styles.slideShow__image}
                src={slide}
                loading={index === 0 ? "eager" : "lazy"}
                {...({ fetchpriority: index === 0 ? "high" : "low" } as ImagePriorityProps)}
                decoding="async"
                alt={slideAlts[index] || "Newborn baby in a softly styled portrait setting"}
              />
            </SwiperSlide>
          );
        })}
      </Swiper>
      <button className={stylePrevBtn} title="prev slide">
        &#10094;
      </button>
      <button className={styleNextBtn} title="next slide">
        &#10095;
      </button>
    </div>
  );
};
